import React, { useEffect, useState, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import { ChatBulkAuditChatRendererPropsV2 } from './chatBulkAuditChatRendererProps';
import _ from 'lodash';
import { TaskType } from '@scale/shared-types';
import TaskComponent from 'react-scale/components/Task/TaskComponent';
import { ChatV2TaskProps } from 'react-scale/components/scalers/tasks/ChatV2/types';
import { ChatResponseV2, MultiTurnInteractionConfigurationV2 } from '@scale/shared/types/chat';
import { BaseParams } from '@scale/shared/types/params';
import ConfirmModal from './ConfirmModal';
import { ChatRendererHeader } from './ChatRendererHeader';
import { useTranslationManagement } from './hooks/useTranslationManagement';
import { useEditManagement } from './hooks/useEditManagement';
import {
  withChatBulkAuditErrorBoundary,
  useErrorBoundarySetup,
} from './ChatBulkAuditErrorBoundary';
import { useChatLayoutStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatLayoutStore';
import { useWindowSize, useMeasure } from 'react-use-v13';
import { feedbackBarWidth } from './layoutHelpers';
import { useChatResponseCompletionStatusStore } from './hooks/useChatResponseCompletionStatusStore';

/**
 * V2 Chat Renderer - handles TaskType.ChatExperimental and TaskType.ChatV2
 */
const ChatBulkAuditChatRendererV2Internal = (props: ChatBulkAuditChatRendererPropsV2) => {
  const [responses, setResponses] = useState(props.responses);
  const setCompletionStatusForAttemptAuditId = useChatResponseCompletionStatusStore(
    state => state.setCompletionStatusForAttemptAuditId,
  );
  const useMultiReplica = responses && 'replicas' in responses;

  useErrorBoundarySetup(props.auditAttemptId);

  const { layout } = useChatLayoutStore();
  const { width: windowWidth } = useWindowSize();
  const [ref, { width: divWidth }] = useMeasure();

  // Translation management
  const {
    showTranslatedResponses,
    translatedTask,
    translatedResponses,
    translatedParams,
    isLoadingTranslatingResponses,
    handleTranslationToggle,
  } = useTranslationManagement({
    responses,
    params: props.params,
    readOnly: props.readOnly,
    auditAttemptId: props.auditAttemptId,
  });

  // Edit management
  const {
    submitEdit,
    handleSubmitRevert,
    handleConfirmRevert,
    handleCancelRevert,
    showConfirmRevertModal,
    debouncedSubmitEdit,
  } = useEditManagement({
    auditAttemptId: props.auditAttemptId,
    originalResponses: props.responses,
    readOnly: props.readOnly,
  });

  // Update local responses when props change
  useEffect(() => {
    setResponses(props.responses);
  }, [props.responses]);

  // Handle multi-replica submission manually
  const handleMultiReplicaSubmit = useMemo(() => {
    if (useMultiReplica) {
      return async () => {
        if (!_.isEqual(responses, props.responses)) {
          await submitEdit(responses);
        }
      };
    }
    return undefined;
  }, [useMultiReplica, responses, props.responses, submitEdit]);

  // Auto-save for non-multi-replica
  useEffect(() => {
    if (!props.readOnly && !useMultiReplica && !_.isEqual(responses, props.responses)) {
      debouncedSubmitEdit(responses as ChatResponseV2);
    }
    return () => {
      debouncedSubmitEdit.cancel();
    };
  }, [responses, props.responses, debouncedSubmitEdit, props.readOnly, useMultiReplica]);

  // Prepare task props
  const taskProps: ChatV2TaskProps = {
    isChatBulkAuditMode: true,
    subtasks: [
      {
        attempts: [
          {
            response: responses,
          },
        ],
      },
    ],
    // Override assignment to modify chatTaskFeatureConfig for copying
    assignment: props.assignment?.projectConfig
      ? {
          ...props.assignment,
          projectConfig: {
            ...props.assignment.projectConfig,
            chatTaskFeatureConfig: {
              ...props.assignment.projectConfig.chatTaskFeatureConfig,
              disableCopying: {
                prompt: {
                  enabledReviewLevels: [],
                },
                responseSelector: {
                  enabledReviewLevels: [],
                },
              },
              disablePasting: {
                prompt: {
                  enabledReviewLevels: [],
                },
                response: {
                  enabledReviewLevels: [],
                },
                rubricCriteriaBuilder: {
                  enabledReviewLevels: [],
                },
                allTextCollectionFields: {
                  enabledReviewLevels: [],
                },
              },
            },
          },
        }
      : props.assignment,
    params:
      showTranslatedResponses && translatedParams && translatedResponses
        ? (translatedParams as BaseParams & MultiTurnInteractionConfigurationV2)
        : (props.params as BaseParams & MultiTurnInteractionConfigurationV2),
    response:
      showTranslatedResponses && translatedResponses && translatedParams
        ? translatedResponses
        : responses,
    showOutline: false,
    readOnly: props.readOnly,
    onResponseChange: (responseNew: ChatResponseV2) => {
      setResponses(responseNew);
    },
    onIsStepperFinished: (isFinished: boolean) => {
      if (props.auditAttemptId) {
        setCompletionStatusForAttemptAuditId(props.auditAttemptId, isFinished);
      }
    },
    reviewLevel:
      typeof props.assignment?.reviewLevel === 'number' ? props.assignment?.reviewLevel ?? 10 : 10,
  };

  return (
    <div className="scaleui scale-tailwind scale-theme w-full h-full flex flex-col flex-nowrap bg-utility-white">
      <ToastContainer />
      <div
        className={
          'flex w-full grow overflow-auto ' +
          (layout === 'multicolumn' || divWidth + feedbackBarWidth > windowWidth
            ? 'justify-start'
            : 'justify-center')
        }
      >
        <div className="my-4" ref={ref}>
          <div className="pb-12 h-full">
            <ChatRendererHeader
              readOnly={props.readOnly}
              edited={props.edited}
              editedStepList={props.editedStepList}
              showTranslatedResponses={showTranslatedResponses}
              isLoadingTranslatingResponses={isLoadingTranslatingResponses}
              responses={props.responses}
              translatedTask={translatedTask}
              onRevertEdits={handleSubmitRevert}
              onTranslationToggle={handleTranslationToggle}
            />

            <TaskComponent
              key={`${props.assignment?.reviewLevel}-${showTranslatedResponses}-${props.auditAttemptId}`}
              taskProps={taskProps}
              taskType={TaskType.ChatV2}
              compact={true}
            />

            {useMultiReplica && handleMultiReplicaSubmit && (
              <div className="fixed bottom-4 right-4">
                <button
                  onClick={handleMultiReplicaSubmit}
                  className="bg-primary-500 text-white px-4 py-2 rounded"
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showConfirmRevertModal && (
        <ConfirmModal
          isOpen={showConfirmRevertModal}
          onClose={handleCancelRevert}
          onConfirm={handleConfirmRevert}
          message="Reverted changes cannot be recovered. Do you wish to proceed?"
          actionButtonVariant="danger"
          actionButtonText="Revert"
        />
      )}
    </div>
  );
};

const ChatBulkAuditChatRendererV2 = withChatBulkAuditErrorBoundary(
  ChatBulkAuditChatRendererV2Internal,
);

export default React.memo(ChatBulkAuditChatRendererV2);
